import React, { RefObject, useContext, useMemo, useState } from 'react';
import { CanvasContext } from 'context/canvas-context/canvas-context';
import { FlippedLayer } from 'flipped-layer/flipped-layer';
import { ActiveLayerContext } from 'context/active-layer-context/active-layer-context';

import styles from './text-editor-menu.module.css';
import classNames from 'classnames';
import { Dropdown, UnionOptionType } from 'components/dropdown/dropdown';
import { TextEditorContext } from 'context/text-editor-context/text-editor-context';
import { IconButton } from 'components/Icon-button/icon-button';
import { useSaveText } from 'hooks/use-save-text';
import { COLORS } from 'constants/colors';
import { Menu, MenuItem, MenuSeparator } from 'components/menu';

type Props = {
	textAreaRef: RefObject<HTMLTextAreaElement>;
	onFontSizeChange: (fontSize: number) => void;
	pageX: number;
	pageY: number;
};

const FONT_SIZES = [
	{
		label: 'Tiny',
		value: 8,
	},
	{
		label: 'Small',
		value: 12,
	},
	{
		label: 'Medium',
		value: 14,
	},
	{
		label: 'Big',
		value: 18,
	},
	{
		label: 'Large',
		value: 26,
	},
];

const COLORS_LIST = [
	{
		label: '#70d6ff',
	},
	{
		label: '#ff70a6',
	},
	{
		label: '#ff9770',
	},
	{
		label: '#ffd670',
	},
	{
		label: '#e9ff70',
	},
];

const TEXT_ALIGN: CanvasTextAlign[] = ['left', 'center', 'right'];

/**
 * Menu Component
 */
export const TextEditorMenu: React.FC<Props> = ({
	pageX,
	pageY,
	textAreaRef,
	onFontSizeChange,
}) => {
	const {
		textAlign,
		setTextAlign,
		fontSize,
		setText,
		setFontSize,
		setBold,
		setItalic,
		bold,
		italic,
	} = useContext(TextEditorContext);
	const { renderingManager, renderer } = useContext(CanvasContext);
	const { activeLayer } = useContext(ActiveLayerContext);
	const saveText = useSaveText();
	const backgroundInitialColor = useMemo(() => {
		if (activeLayer) {
			const square = activeLayer.getChildByType(['rect', 'rounded-rect']);
			if (square) {
				const options = square.getOptions();
				return options.color;
			}
		}
		return COLORS.STICKER_BACKGROUND;
	}, [activeLayer]);
	const [backgroundColor, setBackgroundColor] = useState<string>(
		backgroundInitialColor
	);

	const onColorSelect = (option: UnionOptionType<string>) => {
		if (activeLayer) {
			const square = activeLayer.getChildByType(['rect', 'rounded-rect']);
			if (typeof option !== 'string' && option.label) {
				square?.setOptions({ color: option.label });
				setBackgroundColor(option.label);
				renderingManager?.reDraw({
					exceptLayer: activeLayer,
					callBack: () => {
						renderingManager?.draw(activeLayer, {
							excludeType: 'text',
						});
					},
				});
			}
		}
	};

	const onFontSizeSelect = (option: UnionOptionType<number>) => {
		if (typeof option !== 'string' && option.value) {
			setFontSize(option.value);
			onFontSizeChange(option.value);
		}
		textAreaRef?.current?.focus();
	};

	const onFlip = () => {
		if (activeLayer) {
			saveText();
			(activeLayer as FlippedLayer).switch();
			const textChild = activeLayer.getChildByType('text');
			if (textChild) {
				const options = textChild?.getOptions();
				setText(options.text);
			} else {
				setText('');
			}

			renderingManager?.reDrawSync({ exceptLayer: activeLayer });
			renderingManager?.draw(activeLayer, { excludeType: 'text' });
			textAreaRef?.current?.focus();
		}
	};

	const onBold = () => {
		setBold((state) => !state);
		textAreaRef?.current?.focus();
	};

	const onItalic = () => {
		setItalic((state) => !state);
		textAreaRef?.current?.focus();
	};

	const onTextAlign = () => {
		let index = TEXT_ALIGN.indexOf(textAlign);
		index += 1;
		if (index >= TEXT_ALIGN.length) {
			index = 0;
		}

		setTextAlign(TEXT_ALIGN[index]);

		textAreaRef?.current?.focus();
	};

	const selectedFontsSizeLabel: string = useMemo(() => {
		const option = FONT_SIZES.find((option) => option.value === fontSize);
		if (option) {
			return option.label;
		}
		return `Custom: ${fontSize}`;
	}, [fontSize]);
	console.log('selectedFontsSizeLabel', selectedFontsSizeLabel);
	if (!activeLayer || !renderer) return null;

	const type = activeLayer.getType();
	const [w] = activeLayer.getWidthHeight();
	const options = activeLayer.getOptions();
	const transform = renderer.getTransform();

	const inverseScale = 1 / (transform.scaleX / transform.initialScale);
	const scale =
		options.scale !== 1
			? transform.scaleX / transform.initialScale
			: options.scale / inverseScale;

	const renderColorItem = (
		option: UnionOptionType<string>,
		selected: boolean
	) => {
		let value: string;

		if (typeof option === 'string') {
			value = option;
		} else {
			value = option.label;
		}

		if (value) {
			return (
				<button
					className={classNames(styles.colorButton, {
						[styles.selected]: selected,
					})}
					style={{ background: value }}
				/>
			);
		}
		return null;
	};

	return (
		<div
			className={styles.container}
			style={{
				top: `${pageY - 15 * scale}px`,
				left: `${pageX + (w * scale) / 2}px`,
			}}
		>
			{type === 'flipped-layer' && (
				<Menu>
					<MenuItem>
						<IconButton
							fill
							color="main-white"
							onClick={onFlip}
							icon="flip"
						/>
					</MenuItem>
				</Menu>
			)}
			<Menu>
				<MenuItem>
					<Dropdown
						defaultValue={backgroundColor}
						onSelect={onColorSelect}
						options={COLORS_LIST}
						renderItem={renderColorItem}
					/>
				</MenuItem>
				<MenuSeparator />
				<MenuItem selected={bold}>
					<IconButton
						fill
						color="main-white"
						onClick={onBold}
						icon="bold"
					/>
				</MenuItem>
				<MenuItem selected={italic}>
					<IconButton
						fill
						color="main-white"
						onClick={onItalic}
						icon="italic"
					/>
				</MenuItem>
				<MenuItem>
					{textAlign === 'left' && (
						<IconButton
							fill
							color="main-white"
							onClick={onTextAlign}
							icon="text-align-left"
						/>
					)}
					{textAlign === 'center' && (
						<IconButton
							fill
							color="main-white"
							onClick={onTextAlign}
							icon="text-align-center"
						/>
					)}
					{textAlign === 'right' && (
						<IconButton
							fill
							color="main-white"
							onClick={onTextAlign}
							icon="text-align-right"
						/>
					)}
				</MenuItem>
				<MenuSeparator />
				<MenuItem className={styles.itemFonts}>
					<Dropdown
						defaultValue={selectedFontsSizeLabel}
						onSelect={onFontSizeSelect}
						options={FONT_SIZES}
					/>
				</MenuItem>
			</Menu>
		</div>
	);
};
